import React, { useEffect, useMemo } from 'react';
import { useVesonPnlQuery } from '../../graphql';
import { hasFeature } from '../../state/zustand';
import { toast } from 'react-toastify';
import { Box, Text } from '../ui';
import LoadingSpinner from '../LoadingSpinner';

function useIntegrationVesonPnl() {
  const isEnabled = hasFeature('veson_tools');
  const { data, loading, error } = useVesonPnlQuery({
    skip: !isEnabled,
  });

  useEffect(() => {
    if (!loading && error) {
      toast.error(`Error fetching IMOS PnL ${error.message}`);
    }
  }, [error, loading]);

  const vesonPnl = data?.getVesonPnl?.voyagePnl ?? null;

  return {
    isEnabled,
    isLoading: loading,
    vesonPnl,
  };
}

export function useVoyageVesonPnl(
  vesselName: string,
  externalShipmentIds: string[],
) {
  const { isEnabled, isLoading, vesonPnl } = useIntegrationVesonPnl();

  const pnl = useMemo(() => {
    const voyageNumbers: number[] = [];

    externalShipmentIds.forEach((extId) => {
      const externalIdParts = extId.split('-');
      if (externalIdParts.length < 2) {
        console.warn(
          'Bad shipment external id, unable to parse voyage number',
          extId,
        );
        return null;
      }

      const voyageNumber = Number(externalIdParts.at(-1));
      voyageNumbers.push(voyageNumber);
    });

    const voyagePnls =
      vesonPnl?.filter(
        (voyage) =>
          voyage.VesselName === vesselName &&
          voyageNumbers.includes(voyage.VoyageNo),
      ) ?? [];

    let tce: number | null = null;
    let totalBunkerCost: number | null = null;
    let totalCost: number | null = null;
    let totalFreightIncome: number | null = null;
    let totalExtraFreightIncome: number | null = null;
    let totalCommissions: number | null = null;
    let totalIncome: number | null = null;
    let totalOtherIncome: number | null = null;

    let totalPortCost: number | null = null;
    let totalProfit: number | null = null;
    let totalExtraCost: number | null = null;
    let totalOtherCost: number | null = null;

    voyagePnls.forEach((voyagePnl) => {
      voyagePnl.VoyagePnlSummary.forEach((item) => {
        const description = item.Description?.trim();

        if (item.RootCategory === 'Expenses') {
          totalCost = (totalCost ?? 0) + (item.ActualAmountBase ?? 0);

          if (description === 'Bunkers') {
            totalBunkerCost =
              (totalBunkerCost ?? 0) + (item.ActualAmountBase ?? 0);
            return;
          }

          if (description === 'Port Expenses') {
            totalPortCost = (totalPortCost ?? 0) + (item.ActualAmountBase ?? 0);
            return;
          }

          if (
            description === 'Cargo Expenses' ||
            description === 'Misc. Expenses'
          ) {
            totalExtraCost =
              (totalExtraCost ?? 0) + (item.ActualAmountBase ?? 0);
            return;
          }

          totalOtherCost = (totalOtherCost ?? 0) + (item.ActualAmountBase ?? 0);

          return;
        }

        if (item.RootCategory === 'Revenues') {
          totalIncome = (totalIncome ?? 0) + (item.ActualAmountBase ?? 0);

          if (description === 'Freight') {
            totalFreightIncome =
              (totalFreightIncome ?? 0) + (item.ActualAmountBase ?? 0);
            return;
          }

          if (
            description === 'Misc. Revenues' ||
            description === 'Bunker Adjusted Freight'
          ) {
            totalExtraFreightIncome =
              (totalExtraFreightIncome ?? 0) + (item.ActualAmountBase ?? 0);
            return;
          }

          if (
            description === 'Freight Comm.' ||
            description === 'Freight Add. Comm.'
          ) {
            totalCommissions =
              (totalCommissions ?? 0) + (item.ActualAmountBase ?? 0);
            return;
          }

          totalOtherIncome =
            (totalOtherIncome ?? 0) + (item.ActualAmountBase ?? 0);

          return;
        }

        if (description === 'TCE') {
          tce = item.ActualAmountBase ?? null;
          return;
        }

        if (description === 'Total Profit (Loss)') {
          totalProfit = item.ActualAmountBase ?? null;
          return;
        }
      });
    });

    return {
      tce,
      totalBunkerCost,
      totalCommissions,
      totalCost,
      totalExtraCost,
      totalExtraFreightIncome,
      totalFreightIncome,
      totalIncome,
      totalOtherCost,
      totalOtherIncome,
      totalPortCost,
      totalProfit,
    };
  }, [externalShipmentIds, vesonPnl, vesselName]);

  return {
    isEnabled,
    isLoading,
    pnl,
  };
}

export function IntegrationVesonPnlLoadingIndicator(props: {
  isLoading: boolean;
}) {
  if (!props.isLoading) return null;

  return (
    <Box display="flex" alignItems="center">
      <LoadingSpinner />
      <Text ml={2}>Loading IMOS PnL</Text>
    </Box>
  );
}
